/**
 * LoginPage — split-canvas authentication surface for Seedz Manage.
 *
 * LEFT (60%): the shared VideoBrandPanel — the brand's living 3D form as a
 * full-bleed looping video, hidden below 880px (the mobile lockup below
 * takes over at that point). RIGHT (40%): the credential form on --surface.
 * Fields are §2.16 wells (step DOWN to --surface-inset, no border at rest,
 * 1px accent focus ring); the submit is the screen's single accent pill CTA
 * (§2.8 — lime fill, near-black text via --btn-primary-fg, no raw hex).
 *
 * Error handling reads the server envelope `error.code` for FOUR distinct
 * states: INVALID_CREDENTIALS, ACCOUNT_LOCKED, RATE_LIMIT_EXCEEDED,
 * ACCOUNT_INACTIVE — each with a left-accent tinted alert row (§2.21).
 *
 * On success:
 *  - mustChangePassword=true  → /change-password
 *  - otherwise                → / (app dashboard)
 */
import { useState, useId, type CSSProperties } from 'react';
import { useNavigate } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff, User, Lock } from 'lucide-react';
import { useLogin } from '@/api/auth';
import { VideoBrandPanel, BrandMark } from '@/components/auth/AuthBrandPanel';

// ---------------------------------------------------------------------------
// Validation schema
// ---------------------------------------------------------------------------
const loginSchema = z.object({
  // The identifier may be an email or a plain username (e.g. "admin").
  email: z.string().min(1, 'Username is required'),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormValues = z.infer<typeof loginSchema>;

// ---------------------------------------------------------------------------
// Error-code → human message map
// ---------------------------------------------------------------------------
const ERROR_MESSAGES: Record<string, string> = {
  INVALID_CREDENTIALS: 'Email or password is incorrect.',
  ACCOUNT_LOCKED:
    'This account has been locked after multiple failed attempts. Contact your administrator.',
  RATE_LIMIT_EXCEEDED:
    'Too many sign-in attempts. Please wait a few minutes before trying again.',
  ACCOUNT_INACTIVE:
    'This account is inactive. Contact your administrator to restore access.',
};

function resolveErrorMessage(code: string, fallback: string): string {
  return ERROR_MESSAGES[code] ?? fallback;
}

// ---------------------------------------------------------------------------
// AuthErrorAlert — §2.21 alert row: 3px left accent + same-hue ~10% tint.
// Uses the system-error token (RAG health hues stay reserved for health).
// ---------------------------------------------------------------------------
interface AuthErrorAlertProps {
  code: string;
  message: string;
}

function AuthErrorAlert({ code, message }: AuthErrorAlertProps) {
  const displayMessage = resolveErrorMessage(code, message);

  return (
    <div
      role="alert"
      aria-live="assertive"
      style={{
        padding: '11px 14px',
        borderRadius: 'var(--radius-md)',
        borderLeft: '3px solid var(--error)',
        background: 'color-mix(in srgb, var(--error) 10%, transparent)',
      }}
    >
      <p
        style={{
          margin: 0,
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--text-sm)',
          color: 'var(--error)',
          fontWeight: 500,
        }}
      >
        {displayMessage}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared field styling — the §2.16 well kit.
// Wells step DOWN in luminance (--surface-inset on --surface); no border at
// rest; focus = the shared --ring token (tokens.css; QA R2 fixed its alpha
// to 0.85 so it clears 3:1 non-text contrast in BOTH themes — a local
// fixed-alpha color-mix here would drift back to the theme-blind failure);
// error = 1px system-error ring.
// ---------------------------------------------------------------------------
const FOCUS_RING = 'var(--ring)';
const ERROR_RING = '0 0 0 1px var(--error)';

const labelStyle: CSSProperties = {
  fontFamily: 'var(--font-sans)',
  fontSize: 'var(--text-xs)',
  fontWeight: 500,
  color: 'var(--text-muted)',
};

const inputBase: CSSProperties = {
  width: '100%',
  height: 40,
  fontFamily: 'var(--font-sans)',
  fontSize: 'var(--text-sm)',
  color: 'var(--text)',
  background: 'var(--surface-inset)',
  border: 'none',
  // §1.3 --radius-inset: input wells sit at 12px, not the app-global 14px.
  borderRadius: 'var(--radius-inset)',
  outline: 'none',
  boxSizing: 'border-box',
  transition: 'box-shadow var(--dur-fast) var(--ease-out), background var(--dur-fast) var(--ease-out)',
};

function leadingIconStyle(): CSSProperties {
  return {
    position: 'absolute',
    left: 13,
    top: '50%',
    transform: 'translateY(-50%)',
    color: 'var(--text-subtle)',
    pointerEvents: 'none',
    display: 'flex',
  };
}

// ---------------------------------------------------------------------------
// Primary CTA — §2.8 lime pill: flat fill, near-black text, hover = one
// luminance step (bg only), press = scale 0.98. No shadows, no gradients.
// ---------------------------------------------------------------------------
const CTA_HOVER_BG = 'color-mix(in srgb, var(--btn-primary-bg) 90%, var(--seedz-ink))';

// ---------------------------------------------------------------------------
// LoginPage
// ---------------------------------------------------------------------------
export function LoginPage() {
  const navigate = useNavigate();
  const loginMutation = useLogin();
  const [showPassword, setShowPassword] = useState(false);
  const [serverError, setServerError] = useState<{ code: string; message: string } | null>(null);

  const emailId = useId();
  const passwordId = useId();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
  });

  const isPending = isSubmitting || loginMutation.isPending;

  async function onSubmit(values: LoginFormValues) {
    setServerError(null);
    try {
      const result = await loginMutation.mutateAsync({ email: values.email, password: values.password });
      if (result.mustChangePassword) {
        navigate('/change-password', { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    } catch (err: unknown) {
      const e = err as { code?: string; message?: string };
      setServerError({
        code: e?.code ?? 'UNKNOWN',
        message: e?.message ?? 'An unexpected error occurred. Please try again.',
      });
    }
  }

  return (
    <div
      className="auth-screen"
      style={{ display: 'flex', minHeight: '100vh', minWidth: 0, overflow: 'hidden' }}
    >
      <VideoBrandPanel />

      {/* ------------------------------------------------------------------ */}
      {/* RIGHT PANEL — form surface                                           */}
      {/* ------------------------------------------------------------------ */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '48px 32px',
          background: 'var(--surface)',
          minWidth: 0,
        }}
      >
        <div style={{ width: '100%', maxWidth: 360 }}>
          {/* Mobile lockup (hidden at 880px+, the VideoBrandPanel's own
              breakpoint) — carries the accessible brand name */}
          <div className="min-[880px]:hidden" style={{ marginBottom: 32 }}>
            <BrandMark glyphSize={30} label="Seedz Manage" />
          </div>

          {/* Heading + subtext */}
          <div style={{ marginBottom: 30 }} className="reveal-1">
            <h2
              style={{
                margin: '0 0 7px',
                fontFamily: 'var(--font-display)',
                // ~27px H1 per §1.5, hard-coded so both themes keep the hero scale.
                fontSize: '1.75rem',
                fontWeight: 600,
                letterSpacing: '-0.01em',
                color: 'var(--text)',
              }}
            >
              Sign in
            </h2>
            <p
              style={{
                margin: 0,
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--text-sm)',
                color: 'var(--text-muted)',
              }}
            >
              Enter your credentials to access your workspace.
            </p>
          </div>

          {/* Form */}
          <form
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            style={{ display: 'flex', flexDirection: 'column', gap: 18 }}
          >
            {/* Email */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }} className="reveal-2">
              <label htmlFor={emailId} style={labelStyle}>
                Username
              </label>
              <div style={{ position: 'relative' }}>
                <span style={leadingIconStyle()}>
                  <User width={16} height={16} strokeWidth={1.5} aria-hidden="true" />
                </span>
                <input
                  id={emailId}
                  type="text"
                  autoComplete="username"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck={false}
                  placeholder="admin"
                  aria-invalid={!!errors.email}
                  aria-describedby={errors.email ? `${emailId}-error` : undefined}
                  {...register('email')}
                  style={{
                    ...inputBase,
                    padding: '0 12px 0 38px',
                    boxShadow: errors.email ? ERROR_RING : 'none',
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.boxShadow = FOCUS_RING;
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.boxShadow = errors.email ? ERROR_RING : 'none';
                  }}
                />
              </div>
              {errors.email && (
                <p
                  id={`${emailId}-error`}
                  role="alert"
                  style={{ margin: 0, fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--error)' }}
                >
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* Password */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }} className="reveal-3">
              <label htmlFor={passwordId} style={labelStyle}>
                Password
              </label>
              <div style={{ position: 'relative' }}>
                <span style={leadingIconStyle()}>
                  <Lock width={16} height={16} strokeWidth={1.5} aria-hidden="true" />
                </span>
                <input
                  id={passwordId}
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="••••••••••••"
                  aria-invalid={!!errors.password}
                  aria-describedby={errors.password ? `${passwordId}-error` : undefined}
                  {...register('password')}
                  style={{
                    ...inputBase,
                    padding: '0 40px 0 38px',
                    boxShadow: errors.password ? ERROR_RING : 'none',
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.boxShadow = FOCUS_RING;
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.boxShadow = errors.password ? ERROR_RING : 'none';
                  }}
                />
                {/* §4.2 S2 (GhostIconButton precedent): 44×44 hitbox via
                    transparent padding; the visual stays the Ø28 ghost circle
                    (inner span). right:-2 keeps the circle centre 20px from
                    the well edge — same optics as the old right:6 / Ø28. */}
                <button
                  type="button"
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  onClick={() => setShowPassword((v) => !v)}
                  style={{
                    position: 'absolute',
                    right: -2,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 44,
                    height: 44,
                    padding: 0,
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'var(--text-subtle)',
                    transition: 'color var(--dur-fast) var(--ease-out)',
                  }}
                  onMouseEnter={(e) => {
                    const el = e.currentTarget as HTMLButtonElement;
                    el.style.color = 'var(--text)';
                    (el.firstElementChild as HTMLElement).style.background = 'var(--surface-2)';
                  }}
                  onMouseLeave={(e) => {
                    const el = e.currentTarget as HTMLButtonElement;
                    el.style.color = 'var(--text-subtle)';
                    (el.firstElementChild as HTMLElement).style.background = 'transparent';
                  }}
                >
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 28,
                      height: 28,
                      borderRadius: 'var(--radius-pill)',
                      background: 'transparent',
                      transition: 'background var(--dur-fast) var(--ease-out)',
                    }}
                  >
                    {showPassword ? (
                      <EyeOff width={16} height={16} strokeWidth={1.5} aria-hidden="true" />
                    ) : (
                      <Eye width={16} height={16} strokeWidth={1.5} aria-hidden="true" />
                    )}
                  </span>
                </button>
              </div>
              {errors.password && (
                <p
                  id={`${passwordId}-error`}
                  role="alert"
                  style={{ margin: 0, fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--error)' }}
                >
                  {errors.password.message}
                </p>
              )}
            </div>

            {/* Server error — distinct per error.code */}
            {serverError && (
              <div className="reveal-1">
                <AuthErrorAlert code={serverError.code} message={serverError.message} />
              </div>
            )}

            {/* Submit — the screen's single lime pill CTA */}
            <button
              type="submit"
              disabled={isPending}
              className="reveal-4 focus-ring"
              style={{
                height: 44,
                width: '100%',
                marginTop: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 9,
                padding: '0 20px',
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--text-base)',
                fontWeight: 600,
                letterSpacing: '0.01em',
                color: 'var(--btn-primary-fg)',
                background: 'var(--btn-primary-bg)',
                border: 'none',
                borderRadius: 'var(--radius-pill)',
                cursor: isPending ? 'not-allowed' : 'pointer',
                transition: 'background var(--dur-fast) var(--ease-out), transform var(--dur-fast) var(--ease-out), opacity var(--dur-fast) var(--ease-out)',
                opacity: isPending ? 0.7 : 1,
              }}
              onMouseEnter={(e) => {
                if (!isPending) {
                  (e.currentTarget as HTMLButtonElement).style.background = CTA_HOVER_BG;
                }
              }}
              onMouseLeave={(e) => {
                const el = e.currentTarget as HTMLButtonElement;
                el.style.background = 'var(--btn-primary-bg)';
                el.style.transform = 'none';
              }}
              onMouseDown={(e) => {
                if (!isPending) {
                  (e.currentTarget as HTMLButtonElement).style.transform = 'scale(0.98)';
                }
              }}
              onMouseUp={(e) => {
                (e.currentTarget as HTMLButtonElement).style.transform = 'none';
              }}
            >
              {isPending ? (
                <>
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    aria-hidden="true"
                    style={{ animation: 'spin 0.7s linear infinite', flexShrink: 0 }}
                  >
                    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                  </svg>
                  Signing in…
                </>
              ) : (
                'Sign in'
              )}
            </button>
          </form>

          {/* Enterprise footer */}
          <div className="reveal-5" style={{ marginTop: 28 }}>
            <div style={{ height: 1, background: 'var(--border)', marginBottom: 14 }} />
            <p
              style={{
                margin: '0 0 6px',
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--text-xs)',
                color: 'var(--text-subtle)',
              }}
            >
              <Lock width={12} height={12} strokeWidth={1.5} aria-hidden="true" style={{ flexShrink: 0 }} />
              Authorized access only — all activity is logged and audited.
            </p>
            <p
              style={{
                margin: 0,
                fontFamily: 'var(--font-mono)',
                fontSize: 'var(--text-2xs)',
                letterSpacing: '0.12em',
                // No opacity multiplier: --text-subtle alone clears the 3:1
                // secondary floor in both themes (§4.1 #10); 0.85× pushed the
                // light theme to ~2.9:1.
                color: 'var(--text-subtle)',
              }}
            >
              SEEDZ MANAGE · v1.0
            </p>
          </div>
        </div>
      </div>

      {/* Spinner keyframe + auth-scoped token fixes */}
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        /* §2.16 placeholder --text-low role: token colour (≥4.5:1 on the well
           in BOTH themes) instead of the untokenized preflight gray.
           opacity:1 cancels Firefox's default placeholder dimming. Scoped to
           this surface — the app-global rule belongs in index.css. */
        .auth-screen input::placeholder { color: var(--text-muted); opacity: 1; }
        /* §1.7 motion band: entrance reveals run at --dur (200ms base), not
           --dur-slow (340ms). Reduced-motion (!important in index.css) still
           wins over this override. */
        .auth-screen .reveal-1, .auth-screen .reveal-2, .auth-screen .reveal-3,
        .auth-screen .reveal-4, .auth-screen .reveal-5 {
          animation-duration: var(--dur);
        }
      `}</style>
    </div>
  );
}
